import type { CoverLetter, LetterAnalysis, LetterChange, LetterImproveDiagnostic } from '../types';
import { compterMotsLettre, interligneDe } from './letterText';

/**
 * Fonctions PURES de « Améliorer ma lettre » : ce qui part au serveur, et comment les modifications
 * retenues s'appliquent à la lettre. Comme pour le CV, une modification ne s'applique que si le texte
 * visé est toujours celui que le serveur a lu (`before`) : une désynchronisation ne doit jamais
 * écraser un autre passage.
 */

export interface LettreAmeliorable {
  objet: string;
  paragraphes: string[];
}

/**
 * Ce que l'on envoie à améliorer. Seule une lettre GÉNÉRÉE porte un objet réel : sur une lettre
 * collée ou importée, `objet` n'est que le titre donné dans « Mes lettres » (la formule d'appel, que
 * seule une génération connaît, est alors vide). On n'y propose aucun « objet » à réécrire.
 */
export function lettreAmeliorable(lettre: CoverLetter): LettreAmeliorable {
  const aUnObjetReel = (lettre.lettre.formule_appel ?? '').trim() !== '';
  return { objet: aUnObjetReel ? lettre.objet : '', paragraphes: lettre.lettre.paragraphes };
}

/** Réduit l'analyse à ce qui oriente les modifications. Les analyses anciennes n'ont pas tous les champs. */
export function diagnosticPourAmelioration(analyse: LetterAnalysis): LetterImproveDiagnostic {
  return {
    priorites: analyse.priorites ?? [],
    erreurs: (analyse.erreurs_detectees ?? [])
      .map((e) => ({ extrait: e.extrait ?? '', correction: e.correction_suggeree ?? '' }))
      .filter((e) => e.extrait || e.correction),
    motsClesManquants: analyse.adequation_poste?.mots_cles_manquants ?? [],
    pointsForts: analyse.points_forts ?? [],
    voixCandidat: analyse.voix_candidat ?? [],
    incoherences: (analyse.incoherences_avec_cv ?? []).map((i) => `${i.affirmation} : ${i.constat}`),
  };
}

export function modificationApplicableLettre(lettre: CoverLetter, m: LetterChange): boolean {
  if (m.target === 'subject') return lettre.objet === m.before;
  return m.paragraph !== undefined && lettre.lettre.paragraphes[m.paragraph] === m.before;
}

/** Combien de modifications retenues s'appliqueront vraiment. */
export function nombreAppliqueesLettre(lettre: CoverLetter, changes: LetterChange[], retenues: ReadonlySet<string>): number {
  return changes.filter((m) => retenues.has(m.id) && modificationApplicableLettre(lettre, m)).length;
}

/**
 * Applique les modifications retenues et RIEN d'autre. Le nombre de mots et l'interligne sont
 * recalculés : le rendu PDF d'une lettre plus longue ne doit pas déborder de la page. La lettre
 * d'entrée n'est pas modifiée.
 */
export function appliquerModificationsLettre(
  lettre: CoverLetter,
  changes: LetterChange[],
  retenues: ReadonlySet<string>,
): CoverLetter {
  const paragraphes = [...lettre.lettre.paragraphes];
  let objet = lettre.objet;
  for (const m of changes) {
    if (!retenues.has(m.id) || !modificationApplicableLettre(lettre, m)) continue;
    if (m.target === 'subject') objet = m.after;
    else paragraphes[m.paragraph as number] = m.after;
  }
  const mots = compterMotsLettre(paragraphes);
  return { ...lettre, objet, lettre: { ...lettre.lettre, paragraphes }, nombre_mots: mots, interligne: interligneDe(mots) };
}
